import { useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ChildrenType } from "seneca-common/features/parents/features/parent/service/hooks/types";
import { getMySubscriptionTierName } from "seneca-common/features/subscriptions/features/subscription-products/selectors/subscriptionSubscriptionProduct";
import { SUBSCRIPTION_TIERS } from "seneca-common/features/subscriptions/features/subscription-products/state/models/state/subscription-products/subscription-product/productDescription/ProductDescription";
import { getLiveStripeSubscriptionId } from "seneca-common/features/subscriptions/state";
import { useHistory, useLocation } from "seneca-common/utils/hooks/routing";
import { useSelectorOnConditionWithArgs } from "seneca-common/utils/hooks/selectors";

import { getChildName } from "features/parents/utils/getChildName";

import ChooseAccountPage from "./ChooseAccountPage";
import TransferErrorPage from "./TransferErrorPage";
import TransferSuccessPage from "./TransferSuccessPage";

enum TransferModalState {
  choose = "choose",
  success = "success",
  error = "error"
}

export default function ModalPages({
  onEndSessionClick
}: {
  onEndSessionClick: () => void;
}) {
  const history = useHistory();
  const { search } = useLocation();
  const {
    modalState,
    setModalState,
    selectedChild,
    setSelectedChild,
    childDisplayName,
    tierName
  } = useModalStates();

  if (modalState === TransferModalState.choose) {
    return (
      <ChooseAccountPage
        selectedChild={selectedChild}
        setSelectedChild={setSelectedChild}
        onSubmitSuccess={() => {
          setModalState(TransferModalState.success);
          history.push({
            search: "?" + new URLSearchParams({ state: "success" }).toString()
          });
        }}
        onSubmitError={() => setModalState(TransferModalState.error)}
      />
    );
  }

  if (modalState === TransferModalState.success && selectedChild) {
    return (
      <TransferSuccessPage
        displayName={childDisplayName}
        tierName={tierName}
        onContinueClick={onEndSessionClick}
      />
    );
  }

  return <TransferErrorPage />;
}

function useModalStates() {
  const [modalState, setModalState] = useState(TransferModalState.choose);
  const [selectedChild, setSelectedChild] = useState<ChildrenType>();

  const childDisplayName = getChildName({ child: selectedChild });
  const subscriptionToTransferId = useSelector(getLiveStripeSubscriptionId);
  const tierName = useRef<SUBSCRIPTION_TIERS>();
  const tier = useSelectorOnConditionWithArgs(
    subscriptionToTransferId,
    getMySubscriptionTierName,
    subscriptionToTransferId
  ) as SUBSCRIPTION_TIERS | undefined;

  tierName.current ||= tier;

  return {
    modalState,
    setModalState,
    selectedChild,
    setSelectedChild,
    childDisplayName,
    tierName: tierName.current
  };
}
